/**
 * Uploaded Documents — the client side of `apps/api/src/routes/documents.ts`
 * (T7.24, T7.25; design.md § 10a, 7.3).
 *
 * Documents belong to the BLUEPRINT, not to a version. So nothing here is keyed
 * by version, nothing is invalidated when a version is published, and the tab is
 * available on every blueprint rather than only the seeded one.
 *
 * Two things in here are deliberate rather than incidental:
 *
 *   1. `contentUrl` returns a URL, never bytes. The API serves a picture with
 *      `Content-Security-Policy: sandbox` and `X-Content-Type-Options: nosniff`,
 *      which is the whole defence against an uploaded SVG carrying script. Those
 *      headers only apply to a response the browser fetches, so the picture is
 *      referenced by URL and never inlined into the page as a data: URI or an
 *      <svg> element. Inlining it would hand the markup our own origin.
 *
 *   2. Reordering renumbers a whole kind to 0..n-1 and PATCHes only the rows
 *      that actually moved. The API's `update` sets `orderNo` as given and does
 *      not renumber siblings, so swapping two values blindly can collide when
 *      the stored numbers are not contiguous.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type DocumentKind = 'picture' | 'file';

/** One row of the listing. `uploadedAt` is an ISO string on the wire, not a Date. */
export interface BlueprintDocument {
  id: string;
  blueprintId: string;
  kind: DocumentKind;
  heading: string;
  filename: string;
  /** What it will be served as. For a file this is always application/octet-stream. */
  mime: string;
  sizeBytes: number;
  uploadedBy: string;
  /** Joined on by the API. Null where the uploading account has since gone. */
  uploadedByName: string | null;
  uploadedAt: string;
  orderNo: number;
}

/** The API's own prefix (api.ts, BASE). Repeated rather than exported, and checked by the contract test. */
const BASE = '/api/v1';

export const documentKeys = {
  all: ['documents'] as const,
  list: (blueprintId: string) => [...documentKeys.all, blueprintId] as const,
};

/**
 * Where the bytes live. Used as an `<img src>` for a picture and as an `href`
 * for a file, so the API's security headers are the ones the browser applies.
 */
export function contentUrl(blueprintId: string, documentId: string): string {
  return `${BASE}/blueprints/${blueprintId}/documents/${documentId}/content`;
}

export function useDocuments(blueprintId: string | undefined) {
  return useQuery({
    queryKey: documentKeys.list(blueprintId ?? ''),
    queryFn: async ({ signal }) => {
      const body = await api.get<{ documents: BlueprintDocument[] }>(
        `/blueprints/${blueprintId}/documents`,
        signal,
      );
      return body.documents;
    },
    enabled: Boolean(blueprintId),
  });
}

/* --------------------------------------------------------------- mutations */

export function useUpdateDocument(blueprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; heading?: string; orderNo?: number }) =>
      api.patch<{ document: BlueprintDocument }>(
        `/blueprints/${blueprintId}/documents/${input.id}`,
        {
          ...(input.heading === undefined ? {} : { heading: input.heading }),
          ...(input.orderNo === undefined ? {} : { orderNo: input.orderNo }),
        },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: documentKeys.list(blueprintId) }),
  });
}

export function useDeleteDocument(blueprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: true }>(`/blueprints/${blueprintId}/documents/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: documentKeys.list(blueprintId) }),
  });
}

/**
 * Move one document one place within its own kind.
 *
 * The caller passes the documents of that kind in the order they are shown. The
 * new order is renumbered 0..n-1 and only the rows whose number changed are
 * sent — two requests in the ordinary case, more only where the stored numbers
 * had drifted. The requests go in sequence: two PATCHes racing on the same two
 * rows is how a swap ends with both rows holding the same number.
 */
export function useMoveDocument(blueprintId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ordered,
      id,
      delta,
    }: {
      ordered: BlueprintDocument[];
      id: string;
      delta: -1 | 1;
    }) => {
      const from = ordered.findIndex((d) => d.id === id);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= ordered.length) return;

      const next = [...ordered];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);

      for (const [index, doc] of next.entries()) {
        if (doc.orderNo === index) continue;
        await api.patch<{ document: BlueprintDocument }>(
          `/blueprints/${blueprintId}/documents/${doc.id}`,
          { orderNo: index },
        );
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: documentKeys.list(blueprintId) }),
  });
}

/* ----------------------------------------------------------------- helpers */

export const MAX_PICTURE_BYTES = 2 * 1024 * 1024;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** The picture types the API accepts (design.md § 10a, 7.3). */
export const PICTURE_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/svg+xml',
] as const;

export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** British English, and short enough not to wrap the table. */
export function formatUploadedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
